import { cloneDeep } from 'lodash'
import { Dictionary, isNotEmptyElement } from '@feedyou/utils'
import {
  BotFlow,
  CarouselButtonSchema,
  DialogSchema,
  BotMutationDefinition,
  MutationRule,
  StepSchema,
  StepType
} from '@feedyou-ai/feedbot-schema'

import { getCorrectValue, getHeaderMainKeyIndexes } from './convertService'
import { convertCSVToSteps } from './convertCSVToSteps'
import { getMutationCombinations } from './getMutationCombinations'
import { HeaderMainKeys, MUTATION_ID_SEPARATOR, STEP_KEY_SEPARATOR } from './constants'
import { getSubStepTypeIds } from '../getSubStepTypes'

export function convertXLSToSteps(
  tree: BotFlow,
  locale: string,
  rows: string[][]
): BotFlow | Error {
  try {
    const treeCopy = cloneDeep(tree)
    const rowsCopy = cloneDeep(rows).filter(isNotEmptyElement)

    const header = rowsCopy.shift()

    if (!existsHeaderMainKeys(header)) {
      throw new Error('Invalid header: cannot continue with import!')
    }

    checkDuplicity(rowsCopy, header as string[])

    const csvTree = convertCSVToSteps(treeCopy, locale, header || [], rowsCopy)

    const botMutations = csvTree?.mutations || []
    const mutationCombinations = getMutationCombinations(botMutations)

    return getTreeWithMutations(csvTree, rowsCopy, header || [], mutationCombinations)
  } catch (error) {
    return error as Error
  }
}

function getTreeWithMutations(
  tree: BotFlow,
  rows: string[][],
  header: string[],
  mutationCombinations: string[]
) {
  const rowCopy = cloneDeep(rows)
  const treeCopy = cloneDeep(tree)

  const mutations = treeCopy.mutations
  const dialogsCopy: { [k: string]: any } = cloneDeep(treeCopy.dialogs)

  const stepIdsMemory = {}
  const mutationIdsMemory = getMutationIdsMemory(mutations)

  const {
    stepIdIndex,
    stepTypeIndex,
    dialogIdIndex,
    stepValueIndex,
    subStepIdIndex,
    subStepTypeIndex
  } = getHeaderMainKeyIndexes(header)

  const mainHeaderIndexes = [
    stepIdIndex,
    stepTypeIndex,
    dialogIdIndex,
    stepValueIndex,
    subStepIdIndex,
    subStepTypeIndex
  ]

  const mutationHeader = header.filter((_, index) => !mainHeaderIndexes.includes(index))

  rowCopy.forEach((row: string[]) => {
    const stepId = row[stepIdIndex].toString().trim()
    const stepType = row[stepTypeIndex].toString().trim() as StepType
    const dialogId = row[dialogIdIndex].toString().trim()
    const subStepId = row[subStepIdIndex].toString().trim()
    const subStepType = row[subStepTypeIndex].toString().trim()

    const filteredRows = row.filter((_, index) => !mainHeaderIndexes.includes(index))

    const mutationRow = cloneDeep(filteredRows)

    if (!mutationRow.length) {
      return
    }

    const dialog = dialogsCopy[dialogId]
    const stepIndex = dialog?.steps?.findIndex((step: StepSchema) => step.id === stepId)

    if (stepIndex < 0) {
      return
    }

    if (stepIndex === undefined) {
      return
    }

    const step = getStepWithMutations(
      dialog,
      stepType,
      stepIndex,
      subStepId,
      subStepType,
      mutationRow,
      stepIdsMemory,
      mutationHeader,
      mutationIdsMemory,
      mutationCombinations
    )

    dialogsCopy[dialogId].steps[stepIndex] = step
  })

  //@ts-ignore
  treeCopy.dialogs = dialogsCopy

  return treeCopy
}

type MutationIdsMemory = Dictionary<string, string>
function getStepWithMutations(
  dialog: DialogSchema,
  stepType: StepType,
  stepIndex: number,
  subStepId: string,
  subStepType: string,
  mutationRow: string[],
  stepIdsMemory: { [k: string]: boolean },
  mutationHeader: string[],
  mutationIdsMemory: MutationIdsMemory,
  mutationCombinations: string[]
) {
  const mutations: MutationRule[] = []

  mutationHeader.forEach((headerTitle, mutationIndex) => {
    mutations[mutationIndex] = { set: [], test: [] }
    mutations[mutationIndex].set = []
    mutations[mutationIndex].test = []

    const foundMutation = mutationCombinations.find(combination => combination === headerTitle)

    if (!foundMutation) {
      return
    }

    const mutationOptionIds = foundMutation.split(MUTATION_ID_SEPARATOR)

    mutationOptionIds.forEach((mutationOptionId: string, testIndex: number) => {
      const field = mutationIdsMemory[mutationOptionId]
      if (!field) throw new Error('undefined field')

      mutations[mutationIndex].test![testIndex] = { value: mutationOptionId, field: field }
    })

    //@ts-ignore
    //desc: Object is possibly 'undefined'
    mutations[mutationIndex].set[0] = {
      field: stepType,
      value: getCorrectValue(mutationRow[mutationIndex])
    }
  })

  const filteredMutations = mutations.filter(mutation => mutation?.set?.every(set => set.value))

  return getAndSetStepMutation(
    dialog,
    filteredMutations,
    stepIndex,
    subStepId,
    subStepType,
    stepIdsMemory
  )
}

function getAndSetStepMutation(
  dialog: DialogSchema,
  mutations: MutationRule[],
  stepIndex: number,
  subStepId: string,
  subStepType: string,
  stepIdsMemory: { [k: string]: boolean }
) {
  const foundStep = dialog.steps[stepIndex]

  if (!subStepId) {
    return getStepWithMutation(foundStep, mutations, stepIdsMemory)
  }

  const subStepIds = getSubStepTypeIds(subStepType)

  if (subStepIds.length === 1) {
    return getSubStepWithMutation(foundStep, subStepId, mutations, subStepIds, stepIdsMemory)
  }

  if (subStepIds.length === 2) {
    return getSubSubStepWithMutation(foundStep, subStepId, mutations, subStepIds, stepIdsMemory)
  }

  return foundStep
}

function getMutationIdsMemory(mutations: BotMutationDefinition[] = []) {
  return mutations.reduce((output, mutation) => {
    mutation?.options?.forEach(option => (output[option.id] = mutation.id))
    return output
  }, {} as MutationIdsMemory)
}

function existsHeaderMainKeys(header?: string[]): boolean {
  if (!header) {
    return false
  }

  if (!header.includes(HeaderMainKeys.STEP_ID)) {
    return false
  }

  if (!header.includes(HeaderMainKeys.DIALOG_ID)) {
    return false
  }

  if (!header.includes(HeaderMainKeys.STEP_TYPE)) {
    return false
  }

  if (!header.includes(HeaderMainKeys.STEP_VALUE)) {
    return false
  }

  if (!header.includes(HeaderMainKeys.SUB_STEP_ID)) {
    return false
  }

  if (!header.includes(HeaderMainKeys.SUB_STEP_TYPE)) {
    return false
  }

  return true
}

function getStepWithMutation(
  step: StepSchema,
  mutations: MutationRule[],
  stepIdsMemory: { [k: string]: boolean }
): StepSchema {
  const stepCleared = getStepWithClearedMutation(step, stepIdsMemory)

  stepCleared?.mutations?.push(...mutations)

  return stepCleared
}

function getSubStepWithMutation(
  step: StepSchema,
  subStepId: string,
  mutations: MutationRule[],
  subStepIds: string[],
  stepIdsMemory: { [k: string]: boolean }
): StepSchema {
  const stepCopy = cloneDeep(step)
  const [subStepTypeId] = subStepIds

  const subStepIndex = stepCopy[subStepTypeId].findIndex(
    (subStep: StepSchema) => subStep.id === subStepId
  )

  const subStep = stepCopy[subStepTypeId][subStepIndex]
  const subStepCleared = getStepWithClearedMutation(subStep, stepIdsMemory)

  if (!subStepCleared.mutations) {
    subStepCleared.mutations = []
  }

  subStepCleared.mutations.push(...mutations)
  stepCopy[subStepTypeId][subStepIndex] = subStepCleared

  return stepCopy
}

function getSubSubStepWithMutation(
  step: StepSchema,
  subStepId: string,
  mutations: MutationRule[],
  subStepIds: string[],
  stepIdsMemory: { [k: string]: boolean }
): StepSchema {
  const [carousel, buttons] = subStepIds

  if (!step[carousel]) {
    return step
  }

  if (!step.carousel.length) {
    return step
  }

  const stepCopy = cloneDeep(step)

  for (let carouselIndex = 0; carouselIndex < stepCopy[carousel].length; carouselIndex++) {
    const buttonIndex = stepCopy?.[carousel][carouselIndex]?.[buttons]?.findIndex(
      (button: CarouselButtonSchema) => button.id === subStepId
    )

    if (!buttonIndex || buttonIndex < 0) {
      continue
    }

    const subSubStep = stepCopy[carousel][carouselIndex][buttons][buttonIndex]
    const subSubStepCleared = getStepWithClearedMutation(subSubStep, stepIdsMemory)

    if (!subSubStepCleared.mutations) {
      subSubStepCleared.mutations = []
    }

    subSubStepCleared.mutations.push(...mutations)
    stepCopy[carousel][carouselIndex][buttons][buttonIndex] = subSubStepCleared

    return stepCopy
  }

  return stepCopy
}

function getStepWithClearedMutation(
  step: StepSchema,
  stepIdsMemory: { [k: string]: boolean }
): StepSchema {
  const stepCopy = cloneDeep(step)

  const { id: stepId, type: stepType, mutations } = stepCopy || {}

  const hasBeenStepChecked = stepIdsMemory[stepId]

  if (hasBeenStepChecked) {
    return stepCopy
  }

  checkMutations(stepId, stepType!, mutations)

  stepCopy.mutations = []
  stepIdsMemory[stepId] = true

  return stepCopy
}

function checkMutations(stepId: string, stepType: string, mutations?: MutationRule[]): void {
  const isThereMulitpleSets = mutations?.some(
    mutation => mutation?.set?.length && mutation?.set?.length > 1
  )

  if (!isThereMulitpleSets) {
    return
  }

  throw new Error(
    `There is multiple set values in one mutation for step with type: "${stepType}" and id: "${stepId}", Cannot continue with import!`
  )
}

function checkDuplicity(rows: string[][], header: string[]): void {
  const stepDuplicityCounts = getStepDuplicityCounts(rows, header)
  const substepDuplicityCounts = getSubStepDuplicityCounts(rows, header)

  const isSomeDuplicity = getIsSomeDuplicity(stepDuplicityCounts, substepDuplicityCounts)

  if (!isSomeDuplicity) {
    return
  }

  const message = ['', '']

  const stepInfo = getStepInfo(stepDuplicityCounts)
  const subStepInfo = getSubStepInfo(substepDuplicityCounts)

  if (stepInfo) {
    message.push(stepInfo)
  }

  if (subStepInfo) {
    message.push(subStepInfo)
  }

  throw new Error(message.join(' '))
}

function getSubStepInfo(substepDuplicityCounts: Dictionary<string, number> = {}): string {
  const subStepsMerged = getMergedSubstepsByProperties(substepDuplicityCounts)

  return Object.entries(subStepsMerged).map(getMessageForSubStep).join('')
}

function getMessageForSubStep([key, count]: [string, number | undefined]) {
  const [dialogId, , subStepType, subStepId] = key.split(STEP_KEY_SEPARATOR)

  return `sub step "${subStepType}" with id "${subStepId}" is duplicated ${count} times in dialog "${dialogId}" `
}

function getMergedSubstepsByProperties(substepDuplicityCounts: Dictionary<string, number> = {}) {
  return Object.entries(substepDuplicityCounts).reduce(
    (output: Dictionary<string, number>, subStep) => {
      const [subStepKey, counter] = subStep

      const subStepKeys = subStepKey.split(STEP_KEY_SEPARATOR)
      subStepKeys.pop()

      const newKey = subStepKeys.join(STEP_KEY_SEPARATOR)

      if (shouldSetOrResetCounterInDictionary(newKey, counter, output)) {
        output[newKey] = counter
      }

      return output
    },
    {}
  )
}

function shouldSetOrResetCounterInDictionary(
  key: string,
  counter: number | undefined = 0,
  dictionary: Dictionary<string, number>
): boolean {
  if (dictionary[key]) {
    return false
  }

  //is current set sub step duplicity count greater then found duplicity count for property of the same sub step
  if ((dictionary[key] || 0) > counter) {
    return false
  }

  return true
}

function getStepInfo(stepDuplicityCounts: Dictionary<string, number> = {}): string {
  return Object.entries(stepDuplicityCounts).map(getMessageForStep).join('')
}

function getMessageForStep([key, count]: [string, number | undefined]) {
  const [dialogId, stepId, stepType] = key.split(STEP_KEY_SEPARATOR)

  return `step "${stepType}" with id "${stepId}" is duplicated ${count} times in dialog "${dialogId}" `
}

function getSubStepDuplicityCounts(rows: string[][], header: string[]): Dictionary<string, number> {
  const { dialogIdIndex, stepIdIndex, stepTypeIndex, subStepIdIndex, subStepTypeIndex } =
    getHeaderMainKeyIndexes(header)

  const subStepIdsMemory: string[] = []
  const substepDuplicityCounts: Dictionary<string, number> = {}

  for (const row of rows) {
    const stepId = row[stepIdIndex].toString().trim()
    const dialogId = row[dialogIdIndex].toString().trim()
    const stepType = row[stepTypeIndex].toString().trim()
    const subStepId = row[subStepIdIndex].toString().trim()
    const subStepType = row[subStepTypeIndex].toString().trim()

    const stepKey = [dialogId, stepId, subStepType, subStepId, stepType].join(STEP_KEY_SEPARATOR)

    addItems(subStepId, stepKey, subStepIdsMemory, substepDuplicityCounts)
  }

  return substepDuplicityCounts
}

function getStepDuplicityCounts(rows: string[][], header: string[]): Dictionary<string, number> {
  const { dialogIdIndex, stepIdIndex, stepTypeIndex, subStepIdIndex, subStepTypeIndex } =
    getHeaderMainKeyIndexes(header)

  const stepIdsMemory: string[] = []
  const stepDuplicityCounts: Dictionary<string, number> = {}

  for (const row of rows) {
    const stepId = row[stepIdIndex].toString().trim()
    const dialogId = row[dialogIdIndex].toString().trim()
    const stepType = row[stepTypeIndex].toString().trim()
    const subStepId = row[subStepIdIndex].toString().trim()
    const subStepType = row[subStepTypeIndex].toString().trim()

    if (subStepId || subStepType) {
      continue
    }

    const stepKey = [dialogId, stepId, stepType].join(STEP_KEY_SEPARATOR)

    addItems(stepId, stepKey, stepIdsMemory, stepDuplicityCounts)
  }

  return stepDuplicityCounts
}

function addItems(
  stepId: string,
  stepKey: string,
  stepIdsMemory: string[],
  stepDuplicityCounts: Dictionary<string, number>
) {
  if (!stepId) {
    return
  }

  if (!stepIdsMemory.includes(stepKey)) {
    stepIdsMemory.push(stepKey)
    return
  }

  incrementCounter(stepKey, stepDuplicityCounts)
}

function incrementCounter(stepKey: string, stepDuplicityCounts: Dictionary<string, number>) {
  stepDuplicityCounts[stepKey] = (stepDuplicityCounts[stepKey] || 1) + 1
}

function getIsSomeDuplicity(
  stepDuplicityCounts: Dictionary<string, number>,
  substepDuplicityCounts: Dictionary<string, number>
): boolean {
  const isSomeStepIds = !!Object.keys(stepDuplicityCounts).length

  if (isSomeStepIds) {
    return true
  }

  const isSomeSubStepIds = !!Object.keys(substepDuplicityCounts).length

  if (isSomeSubStepIds) {
    return true
  }

  return false
}
